import Component from '@glimmer/component';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import { tracked } from '@glimmer/tracking'; // still needed for error
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { AccountingPaymentExtension as AccountingPaymentModel } from 'tio-data/data/resources/accounting-payment.ext';
import { hash } from '@ember/helper';
import { accountingPaymentsRequest } from 'tio-data/requests/employee-actions';

interface AccountingPaymentsResponse {
  payments?: AccountingPaymentModel[];
  summary?: AccountingPaymentsSummary;
}

interface AccountingPaymentsSummary {
  total?: number | null;
}

interface ContributionsAccountingPaymentsSignature {
  Args: {
    employee: EmployeeModel;
  };
  Blocks: {
    default: [
      {
        paymentData: AccountingPaymentModel[];
        summaryData: AccountingPaymentsSummary;
        isLoading: boolean;
      },
    ];
  };
}

export default class ContributionsAccountingPaymentsComponent extends Component<ContributionsAccountingPaymentsSignature> {
  @service declare store: StoreService;

  @tracked error?: Error;

  responseData = trackedFunction(this, async () => {
    const employee = this.args.employee;

    try {
      // WarpDrive builder read (wave 1.5) — replaces the custom employee
      // adapter `getAccountingPaymentContributions` method.
      // PLURAL /employees/{id}/accounting_payments; demoMode honoured natively.
      const response = await this.store.request(accountingPaymentsRequest(employee.id!));
      return response.content as AccountingPaymentsResponse;
    } catch (e) {
      console.error(e);
      this.error = e as Error;
    }
  });

  get data(): AccountingPaymentsResponse {
    return this.responseData.value || {};
  }

  get paymentsData(): AccountingPaymentModel[] {
    return this.data?.payments || [];
  }

  get summaryData(): AccountingPaymentsSummary {
    return this.data?.summary || { total: null };
  }

  <template>
    {{yield
      (hash
        paymentData=this.paymentsData
        summaryData=this.summaryData
        isLoading=this.responseData.isPending
      )
    }}
  </template>
}
